/**
 * 文字度量与自动换行工具。
 *
 * 该模块是“先量文字、再排布局”的基础能力：所有需要根据真实文字内容
 * 计算节点宽高的地方（见 autoSizeNodes.ts）都应复用这里的度量结果，
 * 避免各处各自估算导致节点尺寸与实际渲染文字不一致。
 *
 * 浏览器环境下使用 canvas 2D 上下文做精确测量；非浏览器环境（SSR/测试）
 * 回退到基于字符宽度的启发式估算（中日韩字符按全角、其余按半角处理）。
 */

export const DEFAULT_FONT_FAMILY =
  'system-ui, -apple-system, PingFang SC, Microsoft YaHei, sans-serif';

export interface TextMetricsOptions {
  fontSize: number;
  fontFamily?: string;
  fontWeight?: number | string;
}

let measureCanvasCtx: CanvasRenderingContext2D | null | undefined;

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (measureCanvasCtx !== undefined) return measureCanvasCtx;
  if (typeof document === 'undefined') {
    measureCanvasCtx = null;
    return measureCanvasCtx;
  }
  const canvas = document.createElement('canvas');
  measureCanvasCtx = canvas.getContext('2d');
  return measureCanvasCtx;
}

function isWideChar(char: string): boolean {
  return /[\u3000-\u9fff\uac00-\ud7a3\uff00-\uffef]/.test(char);
}

function estimateCharWidth(char: string, fontSize: number): number {
  return isWideChar(char) ? fontSize * 1.05 : fontSize * 0.62;
}

/**
 * 测量一段单行文字的像素宽度。
 * 优先使用 canvas 2D 精确测量，非浏览器环境回退到启发式估算。
 */
export function measureTextWidth(
  text: string,
  options: TextMetricsOptions
): number {
  const ctx = getMeasureContext();
  if (ctx) {
    ctx.font = `${options.fontWeight ?? 400} ${options.fontSize}px ${
      options.fontFamily ?? DEFAULT_FONT_FAMILY
    }`;
    return ctx.measureText(text).width;
  }
  let width = 0;
  for (const char of text) {
    if (char === '\n') continue;
    width += estimateCharWidth(char, options.fontSize);
  }
  return width;
}

export interface WrapTextOptions extends TextMetricsOptions {
  /** 单行可用最大宽度（像素） */
  maxWidth: number;
  /** 最大行数，超出后截断并追加省略号；不传则永不截断（允许无限换行） */
  maxLines?: number;
  ellipsis?: string;
}

export interface WrapTextResult {
  lines: string[];
  /** 是否发生了因 maxLines 限制导致的内容截断 */
  truncated: boolean;
}

/**
 * 按字符逐个测量宽度进行换行（对中文场景更可靠，英文场景退化为按字符换行）。
 * text 中的显式 \n 会被当作强制换行处理。
 */
export function wrapLabelText(
  text: string,
  options: WrapTextOptions
): WrapTextResult {
  const { maxWidth, maxLines, ellipsis = '…' } = options;
  const paragraphs = text.split('\n');
  const lines: string[] = [];
  let truncated = false;

  paragraphLoop: for (const paragraph of paragraphs) {
    let current = '';
    for (const char of paragraph) {
      const candidate = current + char;
      if (current && measureTextWidth(candidate, options) > maxWidth) {
        lines.push(current);
        current = char;
        if (maxLines && lines.length >= maxLines) {
          truncated = true;
          break paragraphLoop;
        }
      } else {
        current = candidate;
      }
    }
    lines.push(current);
    if (maxLines && lines.length >= maxLines) {
      break;
    }
  }

  if (maxLines && lines.length >= maxLines) {
    const consumedLength = lines.slice(0, maxLines).join('').length;
    const totalLength = text.replace(/\n/g, '').length;
    if (consumedLength < totalLength) {
      truncated = true;
    }
    lines.length = maxLines;
  }

  if (truncated && lines.length > 0) {
    let last = lines[lines.length - 1];
    while (last && measureTextWidth(`${last}${ellipsis}`, options) > maxWidth) {
      last = last.slice(0, -1);
    }
    lines[lines.length - 1] = `${last}${ellipsis}`;
  }

  return { lines: lines.length > 0 ? lines : [''], truncated };
}
